import type { AnyFormComponent } from '@findigs/ui-kit/src/types/components';
import { json } from '@remix-run/node';
import { withZod } from '@remix-validated-form/with-zod';
import type { ComponentPropsWithoutRef, JSX } from 'react';
import { forwardRef, useCallback, useMemo } from 'react';
import type { Validator, ValidatorError } from 'remix-validated-form';
import { ValidatedForm, useField } from 'remix-validated-form';
import type { ZodObject, ZodRawShape, ZodTypeAny, infer as z_infer } from 'zod';
import { object as z_object } from 'zod';
import { useHydrated } from '../hooks/use-hydrated';
import { coerceToChildrenArray } from './coerce-to-children-array';
import { omitUndefined } from './omit-undefined';

const SLOT_UNIQUE_CONSTRAINT = 'FormSlots must be unique';
const FORM_CHILDREN_CONSTRAINT = 'Form only takes FormSlot as children';

export const FIRST_FIELD = Symbol('FIRST_FIELD');
export const LAST_FIELD = Symbol('LAST_FIELD');

export type ValidationBehavior = 'onBlur' | 'onChange' | 'onSubmit';

export type ValidationBehaviorOptions = Partial<{
  initial: ValidationBehavior;
  whenTouched: ValidationBehavior;
  whenSubmitted: ValidationBehavior;
}>;

const defaultValidationBehavior = {
  initial: 'onSubmit',
  whenTouched: 'onSubmit',
  whenSubmitted: 'onBlur',
} as const satisfies Required<ValidationBehaviorOptions>;

export type AnyFormElement = 'input' | 'select' | 'textarea';

export type AnyFormElementOrComponent = AnyFormElement | AnyFormComponent;

export type FormField<
  Key extends string,
  ElementOrComponent extends AnyFormElementOrComponent
> = {
  name: Key;
  component: ElementOrComponent;
  props: Omit<ComponentPropsWithoutRef<ElementOrComponent>, 'name' | 'error'>;
};

export type FormFieldWithValidation<
  Key extends string,
  SomeZodType extends ZodTypeAny,
  ElementOrComponent extends AnyFormElementOrComponent
> = FormField<Key, ElementOrComponent> & {
  validation: SomeZodType;
};

export type MakeFormSchemaProps<
  K extends readonly string[],
  C extends readonly AnyFormElementOrComponent[],
  Z extends { [Key in K[number]]: ZodTypeAny },
  SubAction extends string | undefined
> = {
  /** [schema, schema shape type] */
  schema: readonly [ZodTypeAny, Z];

  fields: {
    [I in keyof K]: FormField<K[I], CoercedAt<C, I>>;
  } & {
    [I in keyof C]: FormField<CoercedAt<K, I>, C[I]>;
  };

  form: Omit<
    ComponentPropsWithoutRef<
      typeof ValidatedForm<z_infer<ZodObject<Z>>, SubAction>
    >,
    'validator' | 'children' | 'as' | 'formRef'
  > & {
    validationBehavior?: ValidationBehaviorOptions;
  };
};

export type MakeFormProps<
  K extends readonly string[],
  C extends readonly AnyFormElementOrComponent[],
  Z extends readonly ZodTypeAny[],
  SubAction extends string | undefined
> = {
  fields: {
    [I in keyof K]: FormFieldWithValidation<
      K[I],
      CoercedAt<Z, I>,
      CoercedAt<C, I>
    >;
  } & {
    [I in keyof C]: FormFieldWithValidation<
      CoercedAt<K, I>,
      CoercedAt<Z, I>,
      C[I]
    >;
  } & {
    [I in keyof Z]: FormFieldWithValidation<
      CoercedAt<K, I>,
      Z[I],
      CoercedAt<C, I>
    >;
  };

  form: Omit<
    ComponentPropsWithoutRef<
      typeof ValidatedForm<
        z_infer<
          ZodObject<
            SchemaObj_Generic<
              {
                [I in keyof K]: FormFieldWithValidation<
                  K[I],
                  CoercedAt<Z, I>,
                  CoercedAt<C, I>
                >;
              } & {
                [I in keyof C]: FormFieldWithValidation<
                  CoercedAt<K, I>,
                  CoercedAt<Z, I>,
                  C[I]
                >;
              } & {
                [I in keyof Z]: FormFieldWithValidation<
                  CoercedAt<K, I>,
                  Z[I],
                  CoercedAt<C, I>
                >;
              }
            >
          >
        >,
        SubAction
      >
    >,
    'validator' | 'children' | 'as'
  > & {
    validationBehavior?: ValidationBehaviorOptions;
  };
};

export type FormExtendedValidator<T> = Validator<T> & {
  addListener: (
    ev: 'validated',
    cb: (error: boolean, formId?: string) => void
  ) => void;
  removeListener: (
    ev: 'validated',
    cb: (error: boolean, formId?: string) => void
  ) => void;
};

/**
 * Helper function for declaratively defining validated forms in Remix
 *
 * Note 1/3: `Form` only takes `FormSlots` as children. **Passing in anything else will
 * cause an exception to be raised during automated testing and at runtime.**
 *
 * TypeScript won't report this as an error due to a limitation is JSX.
 *
 * Note 2/3: the slots themselves must remain constant, their children on the other hand
 * can be changed freely. **Do not conditionally render `FormSlots`**. If you need such logic
 * keep it constrained within the slots themselves.
 *
 * Note 3/3: slots must be unique. In other words, there should ever only be at most one slot with a
 * given `after="something"` value. **Duplicate slots will cause an exception to be raised during
 * automated testing and at runtime.**
 */
export function makeForm<
  K extends readonly string[],
  C extends readonly AnyFormElementOrComponent[],
  Z extends readonly ZodTypeAny[],
  S extends string | undefined
>(defs: MakeFormProps<K, C, Z, S>) {
  type Fields = typeof defs.fields;
  type SchemaObj = SchemaObj_Generic<Fields>;

  // note: we normally should avoid using `any` as much as possible, but this is one of the very few
  // edge cases where it's necessary. We should be okay, since code has 100% unit test coverage, and
  // the `any` usage is contained to this piece (in other words, this function doesn't "leak"
  // `any`-typed values to other pieces)
  const schema: SchemaObj = {} as never;
  for (const field of defs.fields) {
    if (field.validation) {
      const key = field.name as keyof SchemaObj;
      schema[key] = field.validation as never;
    }
  }

  return makeFormSchema<K, C, SchemaObj, S>({
    ...defs,
    schema: [z_object(schema), schema],
  });
}

/**
 * Helper function for declaratively defining validated forms in Remix (schema version)
 *
 * Note 1/3: `Form` only takes `FormSlots` as children. **Passing in anything else will
 * cause an exception to be raised during automated testing and at runtime.**
 *
 * TypeScript won't report this as an error due to a limitation is JSX.
 *
 * Note 2/3: the slots themselves must remain constant, their children on the other hand
 * can be changed freely. **Do not conditionally render `FormSlots`**. If you need such logic
 * keep it constrained within the slots themselves.
 *
 * Note 3/3: slots must be unique. In other words, there should ever only be at most one slot with a
 * given `after="something"` value. **Duplicate slots will cause an exception to be raised during
 * automated testing and at runtime.**
 */
export function makeFormSchema<
  K extends readonly string[],
  C extends readonly AnyFormElementOrComponent[],
  Z extends { [Key in K[number]]: ZodTypeAny },
  S extends string | undefined
>(defs: MakeFormSchemaProps<K, C, Z, S>) {
  type FormSlotProps = {
    children?: React.ReactNode;
  } & (
    | { before: K[number] | typeof FIRST_FIELD }
    | { after: K[number] | typeof LAST_FIELD }
  );

  const fieldNames = defs.fields.map((f) => f.name) as unknown as K;

  const schema = defs.schema[0] as unknown as ZodObject<
    (typeof defs.schema)[1]
  >;

  const _validator = withZod(schema);

  const validatorListeners = new Set<
    (error: boolean, formId?: string) => void
  >();
  const validator = {
    async validate(...args) {
      const result = await _validator.validate(...args);
      for (const listener of validatorListeners)
        listener(Boolean(result.error), result.formId);
      return result;
    },
    addListener(_, cb) {
      validatorListeners.add(cb);
    },
    removeListener(_, cb) {
      validatorListeners.delete(cb);
    },
  } satisfies FormExtendedValidator<z_infer<typeof schema>>;

  const FormSlot = (props: FormSlotProps) => {
    return props.children;
  };

  type FormProps = typeof defs.form & {
    /**
     * Only `FormSlots` are accepted as children.
     * Anything else will cause an exception to be raised.
     */
    children?: React.ReactElement | React.ReactElement[];
  };

  type MakeFormErrorProps =
    | {
        [Key in K[number]]?: string | readonly string[];
      } & { non_field_errors?: string | readonly string[] };

  type FormValidationResult = { formId?: string; error?: ValidatorError };

  const makeFormError = (
    { formId, error }: FormValidationResult,
    props: MakeFormErrorProps = {},
    init: ResponseInit = { status: 400 }
  ) => {
    const { non_field_errors, ...customErrors } = props;

    const fieldErrors = { ...error?.fieldErrors } as {
      [Key in K[number]]?: string;
    };

    let key: keyof typeof customErrors;
    for (key in customErrors) {
      const v = [fieldErrors[key], customErrors[key]]
        .flat()
        .filter((x) => x != null)
        .map((x) => x?.trim())
        .join(' ');

      fieldErrors[key] = v.length > 0 ? v : undefined;
    }

    const result = {
      formId,
      ...error,
      fieldErrors,
      non_field_errors: non_field_errors
        ? [non_field_errors].flat()
        : undefined,
    };

    return json(result, init);
  };

  const { validationBehavior: _, ...defaultFormProps } = defs.form;

  const Form = forwardRef<HTMLFormElement, FormProps>(
    ({ validationBehavior, ...props }, forwardedRef) => {
      const slotsIndices = useMemo(() => {
        const children = coerceToChildrenArray(props.children);

        const map = {
          before: new Map<K[number] | typeof FIRST_FIELD, number>(),
          after: new Map<K[number] | typeof LAST_FIELD, number>(),
        };

        for (const [i, slot] of children.entries()) {
          if (slot.type !== FormSlot) throw new Error(FORM_CHILDREN_CONSTRAINT);

          const props = slot.props as unknown as FormSlotProps;

          if ('before' in props) {
            if (map.before.has(props.before))
              throw new Error(SLOT_UNIQUE_CONSTRAINT);
            map.before.set(props.before, i);
          }

          if ('after' in props) {
            if (map.after.has(props.after))
              throw new Error(SLOT_UNIQUE_CONSTRAINT);
            map.after.set(props.after, i);
          }
        }

        return map;

        // we're intentionally only computing these once for perf. reasons
        // this won't cause issues since slots are expected to remain stable

        // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
      }, []);

      const finalValidationBehavior = useMemo(
        () => ({
          ...defaultValidationBehavior,
          ...omitUndefined(defs.form.validationBehavior ?? {}),
          ...omitUndefined(validationBehavior ?? {}),
        }),
        [validationBehavior]
      );

      const RenderFields = useCallback(
        ({ children }: { children: FormProps['children'] }) => {
          const result: JSX.Element[] = [];

          const _children = coerceToChildrenArray(children);

          const first = slotsIndices.before.get(FIRST_FIELD);
          if (first != null) result.push(_children[first]);

          for (const field of defs.fields) {
            const slotBefore = slotsIndices.before.get(field.name);
            if (slotBefore != null) result.push(_children[slotBefore]);

            const Component = field.component as C[number];

            // calling hooks here is fine since `defs.fields` is a readonly const,
            // thus order and length are both constant, and so no harm is done

            // eslint-disable-next-line react-hooks/rules-of-hooks
            const { error, getInputProps } = useField(field.name, {
              validationBehavior: finalValidationBehavior,
            });

            const errorProp =
              typeof Component === 'string' || error === undefined
                ? undefined
                : ({ error: { message: error } } as const);

            result.push(
              <Component
                {...getInputProps(field.props as never)}
                key={field.name}
                name={field.name}
                {...errorProp}
              />
            );

            const slotAfter = slotsIndices.after.get(field.name);
            if (slotAfter != null) result.push(_children[slotAfter]);
          }

          const last = slotsIndices.after.get(LAST_FIELD);
          if (last != null) result.push(_children[last]);

          return result;
        },
        [finalValidationBehavior, slotsIndices]
      );

      const hydrated = useHydrated();

      return (
        <ValidatedForm
          noValidate={hydrated}
          {...defaultFormProps}
          {...props}
          validator={validator}
          formRef={forwardedRef as never}
        >
          <RenderFields>{props.children}</RenderFields>
        </ValidatedForm>
      );
    }
  );
  Form.displayName = 'SchemaBasedForm';

  return { defs, fieldNames, schema, validator, makeFormError, Form, FormSlot };
}

// begin: helpers used to derive the `SchemaObj` type from `Fields`
type _SchemaObj_RemapItem<T> = T extends {
  name: infer Key;
  validation: infer SomeZodType;
}
  ? Key extends PropertyKey
    ? { [P in Key]: SomeZodType }
    : never
  : never;

type _SchemaObj_Base<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  F extends readonly FormFieldWithValidation<any, any, any>[]
> = UnionToIntersection<{ [I in keyof F]: _SchemaObj_RemapItem<F[I]> }[number]>;

type SchemaObj_Generic<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  F extends readonly FormFieldWithValidation<any, any, any>[]
> = _SchemaObj_Base<F> extends ZodRawShape ? _SchemaObj_Base<F> : never;
// end: helpers used to derive the `SchemaObj` type from `Fields`
